import { Button, Flex, Modal, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import {
  TransactionFormData,
  TransactionFormErrors,
} from '../../../shared/types';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { Input } from '../../../shared/components/Inputs/Inputs';
import { BetaIcon } from '../../../../lib/components';
import { Fragment, useState } from 'react';
import { DdsField } from './useFetchDdsData';
import useValidateDds from './useValidateDds';

type ImportedReferencesModalProps = {
  appendDds: (dds: DdsField) => void;
};

const ImportedReferencesModal = ({
  appendDds,
}: ImportedReferencesModalProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { t } = useTranslation();
  const {
    control,
    formState: { disabled, errors },
    setValue,
    clearErrors,
  } = useFormContext<TransactionFormData>();
  const { fields, remove, append } = useFieldArray({
    control,
    name: 'modalDds',
  });

  const { validateDds, isValidating } = useValidateDds({
    onSuccessAll: () => {
      setIsModalOpen(false);
    },
    appendDds,
  });

  const emptyDdsField: DdsField = {
    reference: '',
    verification: '',
  };

  const handleOpenModal = () => {
    clearErrors();
    setValue('modalDds', [emptyDdsField]);

    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    remove();
  };

  return (
    <>
      <Button
        variant="outline"
        w="fit-content"
        leftSection={<BetaIcon icon="plus" size="sm" />}
        onClick={handleOpenModal}
        disabled={disabled}
      >
        {t('transactions.form.addReference')}
      </Button>

      <Modal
        opened={isModalOpen}
        onClose={handleCloseModal}
        title={<Text size="xl">{t('transactions.form.importFromTraces')}</Text>}
        centered
      >
        <Text c="dark.6" mb="md">
          {t('transactions.form.insertReference')}
        </Text>
        <Flex direction="column" gap="md">
          {fields.map((field, index) => {
            const error = (errors as TransactionFormErrors)?.root?.modalDds?.[
              index
            ];
            return (
              <Fragment key={field.id}>
                <Flex key={field.id} gap="md">
                  <Input
                    name={`modalDds.${index}.reference`}
                    label={t('transactions.form.referenceNumber')}
                  />
                  <Input
                    name={`modalDds.${index}.verification`}
                    label={t('transactions.form.verificationNumber')}
                  />
                  {fields.length > 1 && (
                    <Button
                      variant="outline"
                      p={8}
                      mt="22px"
                      onClick={() => remove(index)}
                      data-testid={`delete-button-${index}`}
                      style={{
                        flexShrink: 0,
                      }}
                    >
                      <BetaIcon icon="trash" size="sm" />
                    </Button>
                  )}

                  <Button
                    variant="outline"
                    p={8}
                    mt="22px"
                    onClick={() => append(emptyDdsField)}
                    data-testid={`add-button-${index}`}
                    style={{
                      visibility:
                        index !== fields.length - 1 ? 'hidden' : 'visible',
                      flexShrink: 0,
                    }}
                  >
                    <BetaIcon icon="plus" size="sm" />
                  </Button>
                </Flex>
                {error?.message && (
                  <Text c="red" size="xs" mt={-12}>
                    {error?.message}
                  </Text>
                )}
              </Fragment>
            );
          })}
        </Flex>

        <Flex justify="end" gap="md" mt="md">
          <Button variant="outline" onClick={handleCloseModal}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="outline"
            onClick={validateDds}
            loading={isValidating}
          >
            {t('common.add')}
          </Button>
        </Flex>
      </Modal>
    </>
  );
};

export default ImportedReferencesModal;
